import { z } from 'zod';
import {
  PROMPT_TEMPLATE_VARIABLES,
  type PromptTemplateVariableKey,
  type TemplateVariableContext,
} from '../utils/prompt-template-variables.js';
import type { ConnectionProvider } from './data-connections.js';

const USER_METADATA_KEY_PREFIX = 'user.metadata.';
const USER_METADATA_DYNAMIC_TEMPLATE_KEY = 'user.metadata.[prop_name]';

const STATIC_PROMPT_TEMPLATE_VARIABLE_KEYS: ReadonlySet<string> = new Set<string>(
  PROMPT_TEMPLATE_VARIABLES.map((variable) => variable.key).filter(
    (variableKey) => variableKey !== USER_METADATA_DYNAMIC_TEMPLATE_KEY
  )
);

function isPromptTemplateVariableKey(value: string): value is PromptTemplateVariableKey {
  if (STATIC_PROMPT_TEMPLATE_VARIABLE_KEYS.has(value)) {
    return true;
  }
  return (
    value.startsWith(USER_METADATA_KEY_PREFIX) && value.length > USER_METADATA_KEY_PREFIX.length
  );
}

export const SQL_TEMPLATE_VARIABLE_CONTEXT: TemplateVariableContext = 'sql_template';

function isTemplateValueTypeMatch(
  valueType: QueryTemplateValueType,
  value: QueryTemplateInputValue
): boolean {
  switch (valueType) {
    case 'string':
      return typeof value === 'string';
    case 'number':
      return typeof value === 'number';
    case 'boolean':
      return typeof value === 'boolean';
    default:
      return false;
  }
}

export const semanticQueryModeSchema = z.enum(['single_asset', 'join', 'multi_query_merge']);
export type SemanticQueryMode = z.infer<typeof semanticQueryModeSchema>;

export const querySelectRoleSchema = z.enum(['metric', 'field']);
export type QuerySelectRole = z.infer<typeof querySelectRoleSchema>;

export const queryFilterOperatorSchema = z.enum([
  'equals',
  'not_equals',
  'in',
  'not_in',
  'contains',
  'gt',
  'gte',
  'lt',
  'lte',
  'between',
  'set',
  'not_set',
]);
export type QueryFilterOperator = z.infer<typeof queryFilterOperatorSchema>;

export const querySelectItemSchema = z.object({
  memberId: z.string().min(1),
  role: querySelectRoleSchema,
  alias: z.string().optional(),
});
export type QuerySelectItem = z.infer<typeof querySelectItemSchema>;

export const queryFilterSchema = z.object({
  memberId: z.string().min(1),
  operator: queryFilterOperatorSchema,
  value: z.unknown().optional(),
});
export type QueryFilter = z.infer<typeof queryFilterSchema>;

export const queryTimeGranularitySchema = z.enum([
  'day',
  'week',
  'month',
  'quarter',
  'year',
  'weekly',
  'monthly',
  'quarterly',
  'yearly',
]);
export type QueryTimeGranularity = z.infer<typeof queryTimeGranularitySchema>;

export const queryRelativePeriodSchema = z.enum([
  '1w',
  '4w',
  '13w',
  '26w',
  '52w',
  '1m',
  '3m',
  '6m',
  '12m',
  'qtr',
  'qtd',
  'ytd',
  'mtd',
]);
export type QueryRelativePeriod = z.infer<typeof queryRelativePeriodSchema>;

export const queryTimeClauseSchema = z.object({
  memberId: z.string().min(1).optional(),
  granularity: queryTimeGranularitySchema.optional(),
  dateRange: z.union([z.string(), z.tuple([z.string(), z.string()])]).optional(),
  relativePeriod: queryRelativePeriodSchema.optional(),
});
export type QueryTimeClause = z.infer<typeof queryTimeClauseSchema>;

export const querySortDirectionSchema = z.enum(['asc', 'desc']);
export type QuerySortDirection = z.infer<typeof querySortDirectionSchema>;

export const querySortSchema = z.object({
  memberId: z.string().min(1),
  direction: querySortDirectionSchema,
});
export type QuerySort = z.infer<typeof querySortSchema>;

export const queryJoinTypeSchema = z.enum(['one_to_one', 'one_to_many', 'many_to_one', 'many_to_many']);
export type QueryJoinType = z.infer<typeof queryJoinTypeSchema>;

export const queryJoinEdgeSchema = z.object({
  sourceAssetId: z.string().min(1),
  targetAssetId: z.string().min(1),
  relationshipId: z.string().optional(),
  joinType: queryJoinTypeSchema.optional(),
});
export type QueryJoinEdge = z.infer<typeof queryJoinEdgeSchema>;

export const queryJoinStrategySchema = z.enum(['native_join', 'separate_then_merge']);
export type QueryJoinStrategy = z.infer<typeof queryJoinStrategySchema>;

export const queryJoinPlanSchema = z.object({
  path: z.array(queryJoinEdgeSchema),
  strategy: queryJoinStrategySchema,
});
export type QueryJoinPlan = z.infer<typeof queryJoinPlanSchema>;

export const queryContextSchema = z.object({
  timezone: z.string().optional(),
  locale: z.string().optional(),
  tenantId: z.string().optional(),
});
export type QueryContext = z.infer<typeof queryContextSchema>;

export const semanticQueryDraftSchema = z.looseObject({
  connectionId: z.string().nullable(),
  mode: semanticQueryModeSchema,
  rootAssetId: z.string().nullable(),
  assetIds: z.array(z.string()),
  select: z.array(querySelectItemSchema),
  filters: z.array(queryFilterSchema),
  groupBy: z.array(z.string()),
  time: queryTimeClauseSchema.optional(),
  sort: z.array(querySortSchema),
  limit: z.number().int().positive().optional(),
  offset: z.number().int().min(0).optional(),
  parameters: z.record(z.string(), z.unknown()).default({}),
  joinPlan: queryJoinPlanSchema.optional(),
  context: queryContextSchema.optional(),
});
export type SemanticQueryDraft = z.infer<typeof semanticQueryDraftSchema>;

export const dataConnectionQueryKindSchema = z.enum(['template', 'guided']);
export type DataConnectionQueryKind = z.infer<typeof dataConnectionQueryKindSchema>;

export const templateDialectSchema = z.enum(['cube_sql', 'postgres_sql']);
export type TemplateDialect = z.infer<typeof templateDialectSchema>;

export function deriveTemplateDialect(
  connectionProvider: ConnectionProvider
): TemplateDialect | null {
  switch (connectionProvider) {
    case 'CUBEJS':
      return 'cube_sql';
    case 'POSTGRES':
      return 'postgres_sql';
    default:
      return null;
  }
}

export function supportsDataConnectionQueries(
  connectionProvider: ConnectionProvider | undefined
): boolean {
  if (connectionProvider === undefined) {
    return false;
  }
  return deriveTemplateDialect(connectionProvider) !== null;
}

export const queryTemplateValueTypeSchema = z.enum(['string', 'number', 'boolean']);
export type QueryTemplateValueType = z.infer<typeof queryTemplateValueTypeSchema>;

export const queryTemplateInputValueSchema = z.union([z.string(), z.number(), z.boolean()]);
export type QueryTemplateInputValue = z.infer<typeof queryTemplateInputValueSchema>;

/**
 * A `mappedVariable` must reference either a static key from
 * `PROMPT_TEMPLATE_VARIABLES` or a runtime-resolved
 * `user.metadata.<prop_name>` slot. This is the canonical parameter-slot
 * vocabulary shared with the SQL template framework.
 */
export const queryTemplatePredefinedVariableSchema = z
  .string()
  .trim()
  .min(1)
  .refine(isPromptTemplateVariableKey, {
    message:
      'mappedVariable must be a known prompt template variable key or a user.metadata.<prop_name> slot',
  }) as z.ZodType<PromptTemplateVariableKey>;
export type QueryTemplatePredefinedVariable = PromptTemplateVariableKey;

export const queryInputVariableSourceSchema = z.enum([
  'runtime_only',
  'llm_only',
  'runtime_default_llm_override',
]);
export type QueryInputVariableSource = z.infer<typeof queryInputVariableSourceSchema>;

export const queryInputVariableDefinitionSchema = z
  .object({
    name: z.string().trim().min(1),
    type: queryTemplateValueTypeSchema,
    source: queryInputVariableSourceSchema,
    mappedVariable: queryTemplatePredefinedVariableSchema.optional(),
    description: z.string().trim().min(1).optional(),
    required: z.literal(true),
    defaultValue: queryTemplateInputValueSchema.optional(),
    allowedValues: z.array(queryTemplateInputValueSchema).min(1).optional(),
  })
  .superRefine((definition, ctx) => {
    if (definition.source === 'llm_only' && definition.mappedVariable !== undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'LLM-only variables cannot define mappedVariable',
        path: ['mappedVariable'],
      });
    }

    if (
      (definition.source === 'runtime_only' || definition.source === 'runtime_default_llm_override') &&
      definition.mappedVariable === undefined
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Runtime-based variables must define mappedVariable',
        path: ['mappedVariable'],
      });
    }

    if (
      definition.defaultValue !== undefined &&
      !isTemplateValueTypeMatch(definition.type, definition.defaultValue)
    ) {
      ctx.addIssue({
        code: 'custom',
        message: `Default value must be of type ${definition.type}`,
        path: ['defaultValue'],
      });
    }

    if (definition.allowedValues !== undefined) {
      definition.allowedValues.forEach((value, index) => {
        if (!isTemplateValueTypeMatch(definition.type, value)) {
          ctx.addIssue({
            code: 'custom',
            message: `Allowed value must be of type ${definition.type}`,
            path: ['allowedValues', index],
          });
        }
      });

      if (
        definition.defaultValue !== undefined &&
        !definition.allowedValues.some((value) => value === definition.defaultValue)
      ) {
        ctx.addIssue({
          code: 'custom',
          message: 'Default value must be one of allowedValues',
          path: ['defaultValue'],
        });
      }
    }
  });
export type QueryInputVariableDefinition = z.infer<typeof queryInputVariableDefinitionSchema>;

export const inputVariableDefinitionsSchema = z
  .record(z.string().trim().min(1), queryInputVariableDefinitionSchema)
  .superRefine((definitions, ctx) => {
    for (const [definitionKey, definition] of Object.entries(definitions)) {
      if (definition.name !== definitionKey) {
        ctx.addIssue({
          code: 'custom',
          message: `Variable key "${definitionKey}" must match definition name`,
          path: [definitionKey, 'name'],
        });
      }
    }
  });
export type InputVariableDefinitions = z.infer<typeof inputVariableDefinitionsSchema>;

export const sqlTemplateQueryDraftSchema = z.object({
  templateSql: z.string().trim().min(1),
});
export type SqlTemplateQueryDraft = z.infer<typeof sqlTemplateQueryDraftSchema>;

export const managedAssociatedStoreRefSchema = z.object({
  kind: z.literal('managed'),
  sourceId: z.uuid(),
});
export type ManagedAssociatedStoreRef = z.infer<typeof managedAssociatedStoreRefSchema>;

export const adhocAssociatedStoreRefSchema = z.object({
  kind: z.literal('adhoc'),
  identifier: z.string().trim().min(1),
  promptContext: z.string().trim().min(1).optional(),
});
export type AdhocAssociatedStoreRef = z.infer<typeof adhocAssociatedStoreRefSchema>;

export const associatedStoreRefSchema = z.discriminatedUnion('kind', [
  managedAssociatedStoreRefSchema,
  adhocAssociatedStoreRefSchema,
]);
export type AssociatedStoreRef = z.infer<typeof associatedStoreRefSchema>;

export const associatedStoresSchema = z.array(associatedStoreRefSchema);
export type AssociatedStores = z.infer<typeof associatedStoresSchema>;

export const guidedQueryDraftSchema = z.object({
  guidance: z.string().trim().min(1),
});
export type GuidedQueryDraft = z.infer<typeof guidedQueryDraftSchema>;

export const templateQueryReadPayloadSchema = z.object({
  queryKind: z.literal('template'),
  templateDialect: templateDialectSchema,
  queryJson: sqlTemplateQueryDraftSchema,
  inputVariableDefinitions: inputVariableDefinitionsSchema,
  associatedStores: associatedStoresSchema.default([]),
});
export type TemplateQueryReadPayload = z.infer<typeof templateQueryReadPayloadSchema>;

export const guidedQueryReadPayloadSchema = z.object({
  queryKind: z.literal('guided'),
  guidance: z.string().trim().min(1),
  associatedStores: associatedStoresSchema.refine((stores) => stores.length > 0, {
    message: 'Guided queries require at least one associated store',
  }),
});
export type GuidedQueryReadPayload = z.infer<typeof guidedQueryReadPayloadSchema>;

export const dataConnectionQueryPayloadSchema = z.discriminatedUnion('queryKind', [
  templateQueryReadPayloadSchema,
  guidedQueryReadPayloadSchema,
]);
export type DataConnectionQueryPayload = z.infer<typeof dataConnectionQueryPayloadSchema>;

export const dataConnectionQueryReadSchema = dataConnectionQueryPayloadSchema;
export type DataConnectionQueryRead = DataConnectionQueryPayload;

export const templateQueryDraftPayloadSchema = z.object({
  queryKind: z.literal('template'),
  queryJson: sqlTemplateQueryDraftSchema,
  inputVariableDefinitions: inputVariableDefinitionsSchema,
  associatedStores: associatedStoresSchema.default([]),
});
export type TemplateQueryDraftPayload = z.infer<typeof templateQueryDraftPayloadSchema>;

export const guidedQueryDraftPayloadSchema = z.object({
  queryKind: z.literal('guided'),
  guidance: z.string().trim().min(1),
  associatedStores: associatedStoresSchema.refine((stores) => stores.length > 0, {
    message: 'Guided queries require at least one associated store',
  }),
});
export type GuidedQueryDraftPayload = z.infer<typeof guidedQueryDraftPayloadSchema>;

export const dataConnectionQueryDraftPayloadSchema = z.discriminatedUnion('queryKind', [
  templateQueryDraftPayloadSchema,
  guidedQueryDraftPayloadSchema,
]);
export type DataConnectionQueryDraftPayload = z.infer<typeof dataConnectionQueryDraftPayloadSchema>;

export const dataConnectionQueryDraftSchema = dataConnectionQueryDraftPayloadSchema;
export type DataConnectionQueryDraft = DataConnectionQueryDraftPayload;

export const sampleQuestionsSchema = z.array(z.string().trim().min(1)).max(20);

export const dataConnectionQuerySchema = z
  .object({
    id: z.uuid(),
    dataConnectionId: z.uuid(),
    name: z.string(),
    description: z.string(),
    sampleQuestions: sampleQuestionsSchema.default([]),
    isDeleted: z.boolean(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    createdBy: z.string(),
    updatedBy: z.string().nullable(),
  })
  .and(dataConnectionQueryPayloadSchema);
export type DataConnectionQuery = z.infer<typeof dataConnectionQuerySchema>;

export const listDataConnectionQueriesResponseSchema = z.object({
  items: z.array(dataConnectionQuerySchema),
});
export type ListDataConnectionQueriesResponse = z.infer<typeof listDataConnectionQueriesResponseSchema>;

export const createDataConnectionQueryBodySchema = z
  .object({
    name: z.string().trim().min(1),
    description: z.string().optional(),
    sampleQuestions: sampleQuestionsSchema.optional(),
  })
  .and(dataConnectionQueryDraftPayloadSchema);
export type CreateDataConnectionQueryBody = z.infer<typeof createDataConnectionQueryBodySchema>;

const updateTemplateQueryBodySchema = z.object({
  queryKind: z.literal('template'),
  name: z.string().trim().min(1).optional(),
  description: z.string().optional(),
  sampleQuestions: sampleQuestionsSchema.optional(),
  queryJson: sqlTemplateQueryDraftSchema.optional(),
  inputVariableDefinitions: inputVariableDefinitionsSchema.optional(),
  associatedStores: associatedStoresSchema.optional(),
});

const updateGuidedQueryBodySchema = z.object({
  queryKind: z.literal('guided'),
  name: z.string().trim().min(1).optional(),
  description: z.string().optional(),
  sampleQuestions: sampleQuestionsSchema.optional(),
  guidance: z.string().trim().min(1).optional(),
  associatedStores: associatedStoresSchema
    .refine((stores) => stores.length > 0, {
      message: 'Guided queries require at least one associated store',
    })
    .optional(),
});

export const updateDataConnectionQueryBodySchema = z
  .discriminatedUnion('queryKind', [updateTemplateQueryBodySchema, updateGuidedQueryBodySchema])
  .superRefine((value, ctx) => {
    const hasField =
      value.name !== undefined ||
      value.description !== undefined ||
      value.sampleQuestions !== undefined ||
      value.associatedStores !== undefined ||
      (value.queryKind === 'template' &&
        (value.queryJson !== undefined || value.inputVariableDefinitions !== undefined)) ||
      (value.queryKind === 'guided' && value.guidance !== undefined);

    if (!hasField) {
      ctx.addIssue({
        code: 'custom',
        message: 'At least one field must be provided',
      });
    }
  });
export type UpdateDataConnectionQueryBody = z.infer<typeof updateDataConnectionQueryBodySchema>;

export const queryTemplatePreviewInputsSchema = z.record(
  z.string().trim().min(1),
  queryTemplateInputValueSchema
);
export type QueryTemplatePreviewInputs = z.infer<typeof queryTemplatePreviewInputsSchema>;

export const previewDataConnectionQueryBodySchema = z
  .object({
    queryKind: z.literal('template'),
    queryJson: sqlTemplateQueryDraftSchema,
    inputVariableDefinitions: inputVariableDefinitionsSchema,
    previewInputs: queryTemplatePreviewInputsSchema.default({}),
  })
  .superRefine((value, ctx) => {
    const definitions = value.inputVariableDefinitions;
    const inputs = value.previewInputs;

    for (const [variableKey, inputValue] of Object.entries(inputs)) {
      if (!(variableKey in definitions)) {
        ctx.addIssue({
          code: 'custom',
          message: `Unexpected preview input variable "${variableKey}"`,
          path: ['previewInputs', variableKey],
        });
        continue;
      }
      const definition: QueryInputVariableDefinition = definitions[variableKey];

      if (!isTemplateValueTypeMatch(definition.type, inputValue)) {
        ctx.addIssue({
          code: 'custom',
          message: `Preview input "${variableKey}" must be of type ${definition.type}`,
          path: ['previewInputs', variableKey],
        });
      }

      if (definition.source === 'runtime_only') {
        ctx.addIssue({
          code: 'custom',
          message: `Preview input "${variableKey}" is not allowed for runtime-only variables`,
          path: ['previewInputs', variableKey],
        });
      }

      if (
        definition.allowedValues &&
        !definition.allowedValues.some((allowedValue) => allowedValue === inputValue)
      ) {
        ctx.addIssue({
          code: 'custom',
          message: `Preview input "${variableKey}" must be one of allowedValues`,
          path: ['previewInputs', variableKey],
        });
      }
    }

    for (const [variableKey, definition] of Object.entries(definitions)) {
      const hasProvidedInput = variableKey in inputs;

      if (definition.source === 'llm_only' && !hasProvidedInput) {
        ctx.addIssue({
          code: 'custom',
          message: `Missing required preview input variable "${variableKey}"`,
          path: ['previewInputs', variableKey],
        });
      }
    }
  });
export type PreviewDataConnectionQueryBody = z.infer<typeof previewDataConnectionQueryBodySchema>;

export const previewDataConnectionQueryResponseSchema = z.object({
  columns: z.array(z.string()),
  rows: z.array(z.record(z.string(), z.unknown())),
  rowCount: z.number().int().nonnegative(),
  warnings: z.array(z.string()),
});
export type PreviewDataConnectionQueryResponse = z.infer<typeof previewDataConnectionQueryResponseSchema>;
